import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const RestrictionBanner = ({ status, onUpgradeClick }) => {
    const { t, language } = useLanguage();
    const [dismissed, setDismissed] = useState([]);
    
    if (!status) return null;
    
    const { restriction, warnings = [] } = status;
    
    const formatExpiryTime = (expiresAt) => {
        if (!expiresAt) return '';
        const expiry = new Date(expiresAt);
        const locale = language === 'cs' ? 'cs-CZ' : 'en-US';
        return expiry.toLocaleString(locale, { 
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    const getRestrictionContent = () => {
        if (!restriction) return null;
        
        switch (restriction.type) {
            case 'ban':
            case 'block':
                return {
                    icon: (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                    ),
                    title: t('banners.restriction.accessRestricted'),
                    message: restriction.reason || t('banners.restriction.defaultReason'),
                    expiry: restriction.expiresAt ? `${t('banners.restriction.renewal')} ${formatExpiryTime(restriction.expiresAt)}` : null,
                    bgClass: 'bg-red-500/10 border-red-500/30',
                    textClass: 'text-red-400',
                    canDismiss: false
                };
                
            case 'captcha':
                return {
                    icon: (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                    ),
                    title: t('banners.restriction.verificationRequired'),
                    message: t('banners.restriction.verificationMessage'),
                    bgClass: 'bg-orange-500/10 border-orange-500/30',
                    textClass: 'text-orange-400',
                    showCaptcha: true,
                    canDismiss: false
                };
                
            case 'delay':
            case 'throttle':
                return {
                    icon: (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    ),
                    title: t('banners.restriction.requestsSlowed'),
                    message: t('banners.restriction.slowedMessage'),
                    bgClass: 'bg-amber-500/10 border-amber-500/30',
                    textClass: 'text-amber-400',
                    canDismiss: true
                };
                
            case 'warning':
                return {
                    icon: (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    ),
                    title: t('banners.restriction.warning'),
                    message: restriction.reason || t('banners.restriction.warningMessage'),
                    bgClass: 'bg-yellow-500/10 border-yellow-500/30',
                    textClass: 'text-yellow-400',
                    canDismiss: true
                };
                
            default:
                return null;
        }
    };
    
    const restrictionContent = getRestrictionContent();
    
    const visibleWarnings = warnings.filter(w => !dismissed.includes(w.message));
    
    const handleDismiss = (message) => {
        setDismissed(prev => [...prev, message]);
    };

    return (
        <div className="space-y-2">
            {restrictionContent && !dismissed.includes('restriction') && (
                <div className={`relative p-3 rounded-lg border ${restrictionContent.bgClass} animate-fade-in`}>
                    <div className="flex items-start gap-3">
                        <span className={restrictionContent.textClass}>
                            {restrictionContent.icon}
                        </span>
                        <div className="flex-1">
                            <div className={`font-medium ${restrictionContent.textClass}`}>
                                {restrictionContent.title}
                            </div>
                            <div className="text-sm text-slate-400 mt-0.5">
                                {restrictionContent.message}
                            </div>
                            {restrictionContent.expiry && (
                                <div className="text-xs text-slate-500 mt-1">
                                    {restrictionContent.expiry}
                                </div>
                            )}
                            {restrictionContent.showCaptcha && (
                                <button className="mt-2 px-4 py-1.5 text-sm bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30 transition-colors">
                                    {t('banners.restriction.verify')}
                                </button>
                            )}
                        </div>
                        {restrictionContent.canDismiss && (
                            <button 
                                onClick={() => handleDismiss('restriction')}
                                className="text-slate-500 hover:text-slate-300 transition-colors"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
            )}
            
            {visibleWarnings.map((warning, idx) => (
                <WarningBanner 
                    key={`${warning.resource}-${idx}`}
                    warning={warning}
                    onDismiss={() => handleDismiss(warning.message)}
                    onUpgrade={onUpgradeClick}
                    t={t}
                />
            ))}
        </div>
    );
};

const WarningBanner = ({ warning, onDismiss, onUpgrade, t }) => {
    const isError = warning.type === 'error';
    
    return (
        <div className={`relative p-3 rounded-lg border ${isError ? 'bg-red-500/10 border-red-500/30' : 'bg-amber-500/10 border-amber-500/30'} animate-fade-in`}>
            <div className="flex items-center gap-3">
                <span className={isError ? 'text-red-400' : 'text-amber-400'}>
                    {isError ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    )}
                </span>
                <div className="flex-1">
                    <span className={`text-sm ${isError ? 'text-red-400' : 'text-amber-400'}`}>
                        {warning.message}
                    </span>
                </div>
                {isError && (
                    <button
                        onClick={onUpgrade}
                        className="px-3 py-1 text-xs bg-cyan-500/20 text-cyan-400 rounded hover:bg-cyan-500/30 transition-colors"
                    >
                        {t('banners.restriction.upgrade')}
                    </button>
                )}
                <button 
                    onClick={onDismiss}
                    className="text-slate-500 hover:text-slate-300 transition-colors"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default RestrictionBanner;
